import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchDrivers } from "../redux/slices/driverSlice";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import ENDPOINTS from "../utils/constants/endpoints";
import { post } from "../services/api";
import useIsMobile from "../hooks/useIsMobile";
import { withMinWidth } from "../utils/responsive";
import { gridSx } from "../components/FilterList";
import { DASHBOARD_COLORS as C, focusRing } from "../components/DashboardCard";
import { cardStyle, pageStyle } from "../components/busDetails/detailLayout";
import {
  REPORTS,
  REPORT_TABS,
  activeFilterKeys,
  buildBusReportRows,
  buildDefaulterRows,
  emptyFilters,
  exportRowsForScope,
  filterLabel,
  filterReportRows,
  recordTarget,
  reportTabsForRole,
  schoolBuses,
  sortReportRows,
} from "../components/misReports/reportConfig";
import { exportReport, hasNonLatinText } from "../components/misReports/reportExport";
import { ReportCell } from "../components/misReports/ReportCells";
import ReportCardsMobile from "../components/misReports/ReportCardsMobile";
import ExportReportDialog from "../components/misReports/ExportReportDialog";
import SchoolBusesDialog from "../components/misReports/SchoolBusesDialog";

// The shared page style tints every button blue; this page colours its own buttons.
const { "& .MuiButton-root": _pageButtons, ...basePage } = pageStyle;
const listPage = { ...basePage, bgcolor: C.page, color: C.navy };
const listCard = { ...cardStyle, height: "auto", borderColor: C.border, overflow: "hidden" };

const SEARCH_DEBOUNCE_MS = 250;
const allEmptyFilters = () => Object.fromEntries(REPORT_TABS.map((t) => [t, emptyFilters(t)]));

// View pages return with history back; the report's view settings are kept on this page's
// history entry (see viewRecord) and anything unreadable falls back to the defaults.
const restoredView = (saved) => {
  if (!saved || !REPORT_TABS.includes(saved.tab)) return null;
  const filters = allEmptyFilters();
  REPORT_TABS.forEach((t) => {
    Object.keys(filters[t]).forEach((key) => {
      if (typeof saved.filtersByTab?.[t]?.[key] === "string") filters[t][key] = saved.filtersByTab[t][key];
    });
  });
  const page = Number(saved.paginationModel?.page);
  const pageSize = Number(saved.paginationModel?.pageSize);
  return {
    tab: saved.tab,
    search: typeof saved.search === "string" ? saved.search : "",
    filtersByTab: filters,
    sortModel: Array.isArray(saved.sortModel) ? saved.sortModel : [],
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: [10, 20, 50].includes(pageSize) ? pageSize : 10,
    busListId: saved.tab === "Bus Report" && typeof saved.busListId === "string" ? saved.busListId : null,
  };
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: C.primary },
  },
};

const segmentSx = (active) => ({
  height: 34,
  px: 1.5,
  gap: 0.75,
  flexShrink: 0,
  borderRadius: "9px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  whiteSpace: "nowrap",
  color: active ? "#fff" : "#334155",
  bgcolor: active ? C.primary : "transparent",
  boxShadow: active ? "0 1px 2px rgba(37,99,235,.35)" : "none",
  "&:hover": { bgcolor: active ? C.primaryHover : "#fff" },
  ...focusRing,
});

const countSx = (active) => ({
  minWidth: 22,
  px: 0.75,
  borderRadius: 99,
  fontSize: 12,
  lineHeight: "20px",
  fontVariantNumeric: "tabular-nums",
  bgcolor: active ? "rgba(255,255,255,.2)" : "#fff",
  color: active ? "#fff" : C.muted,
  border: `1px solid ${active ? "transparent" : C.border}`,
});

const actionSx = {
  width: { xs: 40, md: 32 },
  height: { xs: 40, md: 32 },
  flexShrink: 0,
  borderRadius: "8px",
  color: C.primary,
  border: `1px solid ${C.border}`,
  bgcolor: "#fff",
  "& svg": { fontSize: 18 },
  "&:hover": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" },
  ...focusRing,
};

// Rows open their record; the hover and keyboard focus tints show which row that is.
const clickableRowsSx = {
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover, & .MuiDataGrid-row:focus-within": { bgcolor: C.primarySoft },
};

// A drag that selected text is not a click on the row.
const isSelectingText = () => Boolean(window.getSelection?.()?.toString());

const toolbarButtonSx = (active) => ({
  height: 38,
  flexShrink: 0,
  px: 1.5,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  color: active ? C.primary : "#334155",
  bgcolor: active ? C.primarySoft : "#fff",
  border: `1px solid ${active ? "#BFDBFE" : C.border}`,
  "&:hover": { bgcolor: C.primarySoft, borderColor: "#BFDBFE" },
  ...focusRing,
});

function ReportEmptyState({ hasQuery, onReset, noun }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", minHeight: 240, p: 3, textAlign: "center" }}>
      <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: C.primarySoft, color: C.primary, display: "grid", placeItems: "center" }}>
        <ManageSearchRoundedIcon />
      </Box>
      <Typography sx={{ fontWeight: 600, color: C.navy, fontSize: 15 }}>{hasQuery ? `No matching ${noun} found` : `No ${noun} in this report yet`}</Typography>
      <Typography sx={{ fontSize: 13, color: C.muted, maxWidth: 360 }}>
        {hasQuery ? "Try a different search term or adjust the filters." : "Records appear here once they are available for your account."}
      </Typography>
      {hasQuery && (
        <Button onClick={onReset} size="small" sx={{ textTransform: "none", fontWeight: 600, color: C.primary, ...focusRing }}>
          Clear search and filters
        </Button>
      )}
    </Stack>
  );
}

export default function SchoolList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { role_id, user } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();
  const [restored] = useState(() => restoredView(location?.state?.misReport));

  const [data, setData] = useState({ School: [], "Police Station": [], Bus: [] });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const tabs = useMemo(() => reportTabsForRole(role_id), [role_id]);
  const showPoliceStations = tabs.includes("Police Station");
  const [selectedTab, setTab] = useState(restored?.tab ?? tabs[0]);
  // A restored or remembered tab the role can't see falls back to the first permitted one.
  const tab = tabs.includes(selectedTab) ? selectedTab : tabs[0];
  const [searchInput, setSearchInput] = useState(restored?.search ?? "");
  const [search, setSearch] = useState(restored?.search ?? "");
  const [filtersByTab, setFiltersByTab] = useState(() => restored?.filtersByTab ?? allEmptyFilters());
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({});
  const [sortModel, setSortModel] = useState(restored?.sortModel ?? []);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: restored?.pageSize ?? 10 });
  // The grid clamps the page while rows are still loading, so a restored page is applied after loading.
  const pendingPage = useRef(restored?.page || 0);
  const [exportOpen, setExportOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  // The Bus Report school whose bus list is open, by its report id; restored when returning from a bus.
  const [busListId, setBusListId] = useState(restored?.busListId ?? null);

  // School and Police Station views read the driver list from the store.
  useEffect(() => {
    dispatch(fetchDrivers());
  }, [dispatch]);

  // Each report API returns its full dataset; one failing report doesn't hide the others.
  const loadReports = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const reportPayload = { token, format: "Json" };
    const busPayload = { token, users_id: user?.users_id || user?.id, role_id, showlist_status: "0", limit: "0", format: "Json" };

    const results = await Promise.allSettled([
      post(ENDPOINTS.MASTER.SCHOOL_LIST, reportPayload),
      showPoliceStations ? post(ENDPOINTS.MASTER.POLICE_STATIONS_LIST, reportPayload) : Promise.resolve({ data: [] }),
      post(ENDPOINTS.BUSESMASTER.FETCH_BUSES, busPayload),
    ]);
    const [school, police, bus] = results.map((r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : []));
    const failed = (r) => r.status === "rejected" || !Array.isArray(r.value?.data) || (r.value?.status && r.value.status !== 'success');
    setData({ School: school, "Police Station": police, Bus: bus });
    setErrors({
      School: failed(results[0]),
      "Police Station": failed(results[1]),
      Bus: failed(results[2]),
      "Bus Report": failed(results[0]) || failed(results[2]),
      Defaulter: failed(results[2]),
    });
    setLoading(false);
  }, [user?.users_id, user?.id, role_id, showPoliceStations]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const report = REPORTS[tab];
  const filters = filtersByTab[tab];

  // __key gives every row a unique grid id, even when the API repeats or omits an id.
  const tabRows = useMemo(() => {
    const keyed = (rows) => rows.map((row, i) => ({ ...row, __key: i }));
    return {
      School: keyed(data.School),
      "Police Station": keyed(data["Police Station"]),
      Bus: keyed(data.Bus),
      "Bus Report": keyed(buildBusReportRows(data.School, data.Bus)),
      Defaulter: keyed(buildDefaulterRows(data.Bus)),
    };
  }, [data]);

  const allRows = tabRows[tab];
  const filteredRows = useMemo(() => filterReportRows(allRows, tab, { search, filters }), [allRows, tab, search, filters]);
  const sortedRows = useMemo(() => sortReportRows(filteredRows, tab, sortModel), [filteredRows, tab, sortModel]);

  // A new category, search, filter or sort always starts again from the first page. Comparing with
  // the previous values (not "skip the first run") keeps a restored page under StrictMode re-runs.
  const lastQuery = useRef({ tab, search, filters, sortModel });
  useEffect(() => {
    const last = lastQuery.current;
    if (last.tab === tab && last.search === search && last.filters === filters && last.sortModel === sortModel) return;
    lastQuery.current = { tab, search, filters, sortModel };
    pendingPage.current = 0;
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
  }, [tab, search, filters, sortModel]);

  useEffect(() => {
    if (loading || !pendingPage.current) return;
    const page = Math.min(pendingPage.current, Math.max(0, Math.ceil(sortedRows.length / paginationModel.pageSize) - 1));
    pendingPage.current = 0;
    setPaginationModel((model) => ({ ...model, page }));
  }, [loading, sortedRows.length, paginationModel.pageSize]);

  const activeKeys = activeFilterKeys(tab, filters);
  const hasQuery = Boolean(search.trim()) || activeKeys.length > 0;

  const changeTab = (next) => {
    if (next === tab || !tabs.includes(next)) return;
    // Search fields differ per report, so the search starts fresh; filters are kept per report.
    setTab(next);
    setSearchInput("");
    setSearch("");
    setSortModel([]);
    setBusListId(null);
  };
  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
  };
  const setTabFilters = (next) => setFiltersByTab((all) => ({ ...all, [tab]: next }));
  const resetAll = () => {
    clearSearch();
    setTabFilters(emptyFilters(tab));
  };

  const openFilters = () => {
    setDraftFilters({ ...filters });
    setFilterOpen(true);
  };
  const applyFilters = () => {
    setTabFilters(draftFilters);
    setFilterOpen(false);
  };
  const resetFilters = () => {
    setDraftFilters(emptyFilters(tab));
    setTabFilters(emptyFilters(tab));
    setFilterOpen(false);
  };

  // Every category opens its record through here. The report view is saved on this page's history
  // entry first, so Back on the details screen returns to the same tab, search, filters and page.
  // recordTab is "Bus" for a bus picked from a Bus Report school, which opens like a Bus tab row.
  const openRecord = (row, recordTab = tab) => {
    const target = recordTarget(recordTab, row);
    if (!target) {
      setNotice({ severity: "error", message: `This ${REPORTS[recordTab].title.toLowerCase()} record can't be opened because the report did not return its ID. Refresh the report and try again.` });
      return;
    }
    if (target.busList) {
      setBusListId(target.id);
      return;
    }
    const misReport = { tab, search: searchInput, filtersByTab, sortModel, paginationModel, busListId };
    navigate(location.pathname, { replace: true, state: { ...location.state, misReport } });
    navigate(target.path, { state: target.state });
  };

  const busListSchool = tab === "Bus Report" && busListId ? allRows.find((row) => recordTarget(tab, row)?.id === busListId) : null;

  const renderAction = (row) => (
    <Tooltip title="View Details">
      <IconButton
        aria-label={`View ${row[report.primaryField] || "record"}`}
        onClick={(e) => {
          e.stopPropagation(); // the row itself also opens the record
          openRecord(row);
        }}
        sx={actionSx}
      >
        <VisibilityOutlinedIcon />
      </IconButton>
    </Tooltip>
  );

  const gridColumns = [
    ...report.columns.map((column) => ({
      field: column.field,
      headerName: column.header,
      flex: column.primary ? 1.5 : column.type === "count" ? 0.7 : column.type === "reasons" ? 1.6 : 1,
      minWidth: column.primary ? 200 : column.type === "count" ? 104 : column.type === "reasons" ? 230 : 128,
      align: column.type === "count" ? "center" : "left",
      headerAlign: column.type === "count" ? "center" : "left",
      renderCell: ({ row }) => <ReportCell column={column} value={row[column.field]} />,
    })),
    { field: "actions", headerName: "Actions", width: 92, minWidth: 92, align: "center", headerAlign: "center", sortable: false, filterable: false, disableColumnMenu: true, renderCell: ({ row }) => renderAction(row) },
  ];

  // ---- Export ----
  const { page, pageSize } = paginationModel;
  const scopeRows = (scope) => exportRowsForScope(scope, { allRows: sortReportRows(allRows, tab, sortModel), sortedRows, page, pageSize });
  const scopeCounts = { page: Math.max(0, Math.min(pageSize, sortedRows.length - page * pageSize)), filtered: sortedRows.length, all: allRows.length };
  const filterSummary = (scope) => {
    if (scope === "all") return [];
    const lines = [];
    if (search.trim()) lines.push(`Search: "${search.trim()}"`);
    activeKeys.forEach((key) => lines.push(filterLabel(tab, key, filters[key], allRows)));
    if (scope === "page") lines.push(`Page ${page + 1} of ${Math.max(1, Math.ceil(sortedRows.length / pageSize))}`);
    return lines;
  };
  const openExport = () => {
    setSearch(searchInput); // export what the search box shows, even mid-debounce
    setExportOpen(true);
  };
  const handleExport = async (format, scope) => {
    const rows = scopeRows(scope);
    const { fileName } = await exportReport({ format, tab, rows, filters: filterSummary(scope) });
    setExportOpen(false);
    setNotice({ severity: "success", message: fileName ? `Exported ${rows.length} ${report.title} records to ${fileName}` : `Sent ${rows.length} ${report.title} records to print` });
  };

  const emptyOverlayProps = { hasQuery, onReset: resetAll, noun: report.noun };
  const emptyState = <ReportEmptyState {...emptyOverlayProps} />;
  const hasFilters = report.filters.length > 0;

  return (
    <Box sx={listPage}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" fontWeight={700} sx={{ fontSize: { xs: 20, sm: 23 }, lineHeight: 1.2, color: C.navy }}>
            MIS Reports
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted }}>View, filter and export verification records</Typography>
        </Box>
        <Button
          onClick={openExport}
          disabled={loading}
          startIcon={<FileDownloadRoundedIcon />}
          aria-label="Export report"
          sx={{
            height: 40,
            flexShrink: 0,
            px: { xs: 1.5, sm: 2 },
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            color: "#fff",
            bgcolor: C.primary,
            boxShadow: "0 1px 2px rgba(16,42,67,.12)",
            "&:hover": { bgcolor: C.primaryHover, boxShadow: "0 4px 10px rgba(37,99,235,.25)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,.85)", bgcolor: "#93C5FD" },
            ...focusRing,
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Export Report
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            Export
          </Box>
        </Button>
      </Stack>

      <Card elevation={0} sx={listCard}>
        {/* Toolbar: report categories on the left, search and filters on the right; stacks on smaller screens. */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            p: { xs: 1.25, sm: 1.5 },
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Box
            role="group"
            aria-label="Report category"
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 0.5,
              p: 0.5,
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "auto",
              bgcolor: "#F1F5F9",
              border: `1px solid ${C.border}`,
              borderRadius: "12px",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              flex: { xs: "1 1 100%", lg: "0 1 auto" },
            }}
          >
            {tabs.map((name) => {
              const isActive = tab === name;
              return (
                <Button key={name} aria-pressed={isActive} onClick={() => changeTab(name)} sx={segmentSx(isActive)}>
                  {name}
                  <Box component="span" sx={countSx(isActive)}>
                    {loading ? "…" : errors[name] ? "—" : tabRows[name].length}
                  </Box>
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: { xs: "1 1 100%", lg: "0 1 460px" } }}>
            <TextField
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && clearSearch()}
              placeholder={report.searchPlaceholder}
              sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], height: 38 } }}
              slotProps={{
                htmlInput: { "aria-label": `Search ${report.title} report` },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: C.muted }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={clearSearch} edge="end">
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip title={hasFilters ? "" : "This report has no filters"}>
              <span>
                <Button
                  onClick={openFilters}
                  disabled={!hasFilters}
                  startIcon={<TuneRoundedIcon />}
                  aria-label={activeKeys.length ? `Filters, ${activeKeys.length} applied` : "Filters"}
                  sx={toolbarButtonSx(activeKeys.length > 0)}
                >
                  Filters
                  {activeKeys.length > 0 && (
                    <Box component="span" sx={{ ml: 0.75, minWidth: 20, px: 0.5, borderRadius: 99, bgcolor: C.primary, color: "#fff", fontSize: 12, lineHeight: "20px" }}>
                      {activeKeys.length}
                    </Box>
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {activeKeys.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, px: { xs: 1.25, sm: 1.5 }, py: 1, borderBottom: `1px solid ${C.border}`, bgcolor: "#FBFCFE" }}>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mr: 0.25 }}>Active filters:</Typography>
            {activeKeys.map((key) => (
              <Chip
                key={key}
                size="small"
                label={filterLabel(tab, key, filters[key], allRows)}
                onDelete={() => setTabFilters({ ...filters, [key]: "" })}
                sx={{ bgcolor: C.primarySoft, color: "#1E40AF", fontWeight: 600, maxWidth: "100%", "& .MuiChip-deleteIcon": { color: "#60A5FA" } }}
              />
            ))}
            <Button size="small" onClick={() => setTabFilters(emptyFilters(tab))} sx={{ textTransform: "none", fontWeight: 600, color: C.muted, minWidth: 0 }}>
              Clear all
            </Button>
          </Box>
        )}

        {errors[tab] ? (
          <Box sx={{ p: 1.5 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadReports}>
                  Retry
                </Button>
              }
            >
              Unable to load the {report.title} report. Check your connection and try again.
            </Alert>
          </Box>
        ) : isMobile ? (
          <ReportCardsMobile
            tab={tab}
            rows={sortedRows}
            loading={loading}
            page={page}
            pageSize={pageSize}
            onPageChange={(next) => setPaginationModel({ ...paginationModel, page: next })}
            renderAction={renderAction}
            onOpen={(row) => !isSelectingText() && openRecord(row)}
            emptyState={emptyState}
          />
        ) : (
          <DataGrid
            key={tab}
            rows={sortedRows}
            getRowId={(row) => row.__key}
            columns={withMinWidth(gridColumns)}
            loading={loading}
            rowHeight={52}
            columnHeaderHeight={44}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableColumnFilter
            slots={{ noRowsOverlay: ReportEmptyState, noResultsOverlay: ReportEmptyState }}
            slotProps={{
              noRowsOverlay: emptyOverlayProps,
              noResultsOverlay: emptyOverlayProps,
              loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
            }}
            aria-label={`${report.title} report`}
            onRowClick={({ row }) => !isSelectingText() && openRecord(row)}
            onCellKeyDown={({ row, field }, event) => {
              // The View button handles its own Enter; any other cell opens the row's record.
              if (event.key !== "Enter" || field === "actions") return;
              event.preventDefault();
              openRecord(row);
            }}
            sx={[gridSx, clickableRowsSx]}
            disableRowSelectionOnClick
          />
        )}
      </Card>

      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: "14px" } } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, pb: 1 }}>
          <Box>
            <Typography component="span" sx={{ display: "block", fontWeight: 700, fontSize: 17, color: C.navy }}>
              Filter {report.title} report
            </Typography>
            <Typography component="span" sx={{ display: "block", fontSize: 13, color: C.muted }}>
              Filters apply to every record in this report, not just the visible page
            </Typography>
          </Box>
          <IconButton onClick={() => setFilterOpen(false)} size="small" aria-label="Close filters" sx={{ color: C.muted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: C.border }}>
          <Stack spacing={1.75} sx={{ pt: 0.5 }}>
            {report.filters.map((filter) => (
              <TextField
                key={filter.key}
                select
                size="small"
                label={filter.label}
                value={draftFilters[filter.key] ?? ""}
                onChange={(e) => setDraftFilters({ ...draftFilters, [filter.key]: e.target.value })}
                sx={inputSx}
              >
                <MenuItem value="">Any</MenuItem>
                {filter.options(allRows).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
          <Button onClick={resetFilters} sx={{ textTransform: "none", fontWeight: 600, color: C.muted }}>
            Reset
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setFilterOpen(false)} sx={{ textTransform: "none", fontWeight: 600, color: "#334155" }}>
              Cancel
            </Button>
            <Button
              onClick={applyFilters}
              variant="contained"
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", bgcolor: C.primary, "&:hover": { bgcolor: C.primaryHover } }}
            >
              Apply
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <ExportReportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        tab={tab}
        scopeCounts={scopeCounts}
        hasQuery={hasQuery}
        isPdfBlocked={(scope) => hasNonLatinText(scopeRows(scope), report.columns, filterSummary(scope))}
        onExport={handleExport}
        fullScreen={isMobile}
      />

      <SchoolBusesDialog
        school={busListSchool}
        buses={busListSchool ? schoolBuses(busListSchool, data.Bus) : []}
        onClose={() => setBusListId(null)}
        onOpenBus={(bus) => openRecord(bus, "Bus")}
        fullScreen={isMobile}
      />

      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={notice?.severity ?? "success"} variant="filled" onClose={() => setNotice(null)} sx={{ width: "100%" }}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
